import { Fragment } from 'react';
import { setMockPianoConfig } from 'services/Piano';
import { setHeadData } from 'utils/metaDataUtils/headData';
import ProductHomePage from 'pages/ProductHome/ProductHomePage';
import getLatestArticlesBySections from 'api/Section/getLatestArticlesBySections';
import type { ArticlePreview } from 'interfaces/content/articles/Articles';
import setWellPageAdConfig from 'services/Ad/config/setWellPageAdConfig';
import type { GetStaticProps } from 'next';
import getLatestArticlesByTag from 'api/Article/getLatestArticlesByTag';
import getLatestArticlesByDestination from 'api/Destination/getLatestArticlesByDestination';
import setPageGtmConfig from 'services/Gtm/setPageGtmConfig';
import { omitKeysArray } from 'utils/objectUtils';
import { getVideosByCategoryFromPortal } from 'services/Video';
import type { Video } from 'interfaces/content/Media';
import SidebarContentPreview from 'components/SidebarContentPreview';
import RightRailSimple from 'components/Ad/AdSlot/RightRailSimple';
import HomePageInStream from 'components/Ad/AdSlot/HomePageSimpleInStream';
import getFooter from 'api/Footer/getFooter';
import {
  getMicrositeConfig,
  type SectionsConfig,
  type MicrositeConfig,
  type DestinationHomepage,
} from 'api/MicrositeConfig/getMicrositeConfig';
import type { HeaderNav, MenuItemProps } from 'interfaces/navigation/Header';
import { wellLogo } from 'icons/WellLogo';
import useBreakpoint from 'hooks/useBreakpoint';
import handleStaticPropsError from 'utils/handleStaticPropsError';
import NotFoundError from 'interfaces/errors/NotFoundError';
import useHeaderCoordinates from 'hooks/useHeaderCoordinates';
import { RightRailFlexArticles } from 'components/Ad/AdSlot';
import getMenu from 'api/Header/getMenu';
import type { FeaturedSecondaryProps } from 'components/Home/FeaturedSecondary';
import type { SecondaryHomepage } from 'interfaces/Home';
import { Microsites, layoutCount } from 'constants/constants';
import type { ListTout } from 'interfaces/content/Ranking';
import { calculatePagePagination, type PagePagination } from 'utils/miscUtils/calculatePageInfo';
import HiddenHeaderTags from 'pages/Home/HomePageStyles';
import { safe, safeAsync } from 'utils/errorHandlingUtils';
import fallbackData from 'lib/fallbackData/commonData';
import type { BreadcrumbsProps } from 'interfaces/navigation/Breadcrumbs';

interface WellProps {
  latestArticles: ArticlePreview[];
  editorsPicks: ArticlePreview[];
  features: { heading: string; articles: ArticlePreview[] }[];
  videos: Video[];
  topics: SectionsConfig;
  pagination: PagePagination;
  latestArticlesSection: ArticlePreview[];
  tout: ListTout;
  subMenu?: MenuItemProps[];
  breadcrumbs: BreadcrumbsProps;
}

const WellHome = ({
  breadcrumbs,
  latestArticles,
  features,
  editorsPicks,
  videos,
  topics,
  pagination,
  latestArticlesSection,
  tout,
  subMenu,
}: WellProps) => {
  const isDesktop = useBreakpoint('hybridMin');
  const isMobile = useBreakpoint('mdMax');
  const { bottom } = useHeaderCoordinates();

  return (
    <>
      <HiddenHeaderTags>
        <h1>Well - Fortune Well </h1>
      </HiddenHeaderTags>
      <ProductHomePage
        breadcrumbs={breadcrumbs}
        pagination={pagination}
        carousel={latestArticles}
        features={features}
        sections={Object.values(topics)}
        subMenu={subMenu}
        sidebar={[
          <SidebarContentPreview
            key='0'
            contentPlacementCD='editors pick hero'
            className='editors-picks no-top-margin'
            title="Editors' Picks"
            items={editorsPicks}
          />,
          <Fragment key='1'>
            {isMobile && <HomePageInStream id={0} />}
            {isDesktop && <RightRailSimple id={0} />}
          </Fragment>,
          <SidebarContentPreview
            key='2'
            contentPlacementCD='right rail video'
            className='video-module'
            title='Videos'
            items={videos}
            type='video'
          />,
          <Fragment key='3'>
            {isMobile && <HomePageInStream id={1} />}
            {isDesktop && (
              <RightRailFlexArticles
                key='sticky-ads'
                id={1}
                stickyTop={bottom}
              />
            )}
          </Fragment>,
        ]}
        platform={Microsites.Well}
        title='Well'
        latestArticlesSection={latestArticlesSection}
        tout={tout}
      />
    </>
  );
};

const getFeaturedArticle = (featuredArticles: ArticlePreview[] | null, latestArticles: ArticlePreview[]) => {
  if (featuredArticles && featuredArticles.length > 0) return featuredArticles;
  return latestArticles.slice(0, 1);
};

export const getStaticProps: GetStaticProps = async (context) => {
  try {
    const { pageNum } = (context.params || {}) as { pageNum: string };
    const currentPageNum = parseInt(pageNum || '1', 10);
    if (Number.isNaN(currentPageNum) || currentPageNum < 1) {
      throw new NotFoundError(`[getHomePageNumber] invalid page number: '${pageNum}'`);
    }
    const footer = await safeAsync(() => getFooter(), fallbackData.footer);
    const menu: HeaderNav = await safeAsync(
      () => getMenu('LEFT_MENU_COM', 'RIGHT_MENU_COM', 'TOPIC_NAVIGATION_COM', Microsites.Well, wellLogo),
      fallbackData.headerNav,
    );
    const subNav = await safeAsync(
      () => getMenu('LEFT_MENU_COM', 'RIGHT_MENU_COM', 'TOPIC_NAVIGATION_WELL', Microsites.Well, wellLogo),
      fallbackData.headerNav,
    );
    const subMenu = subNav?.topicNavigation || null;
    const wellData: MicrositeConfig = await getMicrositeConfig(Microsites.Well);
    const wellHomepage: DestinationHomepage = wellData.homepage;
    const wellSections = wellData.sections;
    const latestArticles = (
      await safeAsync(
        () => getLatestArticlesBySections(Object.keys(wellSections), 1, 0, Microsites.Well),
        fallbackData.articlesData,
      )
    ).posts;
    const editorsPicks = await getLatestArticlesByTag('editors-picks', 4, Microsites.Well);
    const featuredArticles = await getLatestArticlesByTag('featured', 1, Microsites.Well);
    // Featured Articles includes Franchises
    const heroArticles = getFeaturedArticle(featuredArticles, latestArticles);
    const { tout } = wellHomepage;

    let featuresArticlesIds: number[] = [];
    const features = (await Promise.all(
      Object.entries(wellSections).map(
        (section) =>
          new Promise((res, rej) => {
            getLatestArticlesBySections([section[0]], 3, 0, Microsites.Well)
              .then((articles) => {
                featuresArticlesIds = [
                  ...featuresArticlesIds,
                  ...articles.posts.map((article) => article.databaseId),
                ] as number[];
                return res({
                  articles: omitKeysArray(articles.posts, ['eyebrowSectionLabel']),
                  heading: section[1].name,
                  headingLink: wellSections[section[0]].url,
                });
              })
              .catch(() => rej());
          }),
      ),
    )) as FeaturedSecondaryProps[];

    featuresArticlesIds.push(...latestArticles.map((article) => article.databaseId));

    const latestArticlesSection = await safeAsync(
      () =>
        getLatestArticlesByDestination(
          Microsites.Well,
          layoutCount.latest,
          [null],
          [...featuresArticlesIds],
          (currentPageNum - 1) * layoutCount.latest,
        ),
      fallbackData.articlesData,
    );

    const { breadcrumbs } = wellData;
    const totalArticles = latestArticlesSection.pageInfo.totalCount > 0 ? latestArticlesSection.pageInfo.totalCount : 0;
    const pagination = calculatePagePagination(currentPageNum, totalArticles);
    const pageAdConfig = setWellPageAdConfig(wellHomepage);
    const dataLayer = setPageGtmConfig(wellHomepage);
    const headData = safe(
      () =>
        setHeadData(
          {
            ...wellHomepage,
            breadcrumbs,
            canonicalUrl: pageNum ? `${wellHomepage.canonicalUrl}page/${pageNum}/` : wellHomepage.canonicalUrl,
            posts: [
              ...(heroArticles[0] ? [heroArticles[0]] : []),
              ...features.reduce(
                (acc: ArticlePreview[], curr: FeaturedSecondaryProps) => [...acc, ...curr.articles],
                [],
              ),
            ],
            titlePrefix: pagination.pageInfo,
          } as SecondaryHomepage,
          true,
          'Fortune Well',
        ),
      fallbackData.headData,
    );

    const videos = await safeAsync(() => getVideosByCategoryFromPortal('Fortune Well'), fallbackData.videos);

    return {
      props: {
        breadcrumbs,
        dataLayer,
        editorsPicks,
        features,
        footer,
        headData,
        headerNav: menu,
        latestArticles: heroArticles,
        latestArticlesSection: latestArticlesSection.posts,
        pageAdConfig,
        pagination,
        pianoPageConfig: setMockPianoConfig(),
        subMenu,
        topics: wellSections,
        tout,
        videos: videos?.videos,
      },
      revalidate: 60, // In seconds
    };
  } catch (e) {
    return handleStaticPropsError(e as Error);
  }
};

export default WellHome;
